import { useEffect, useRef } from 'react';

type Options = {
  /** Pixel radius around the element that activates the pull */
  radius?: number;
  /** How strongly the element follows the cursor (0..1) */
  strength?: number;
  /** Disable on touch / reduced motion */
  disabled?: boolean;
};

/**
 * Magnetic interaction: when the pointer enters a radius around the element,
 * the element translates toward the pointer proportionally. Returns ref to attach.
 *
 * The element also receives `data-magnetic="true"` so the global CursorTracker
 * can react (expand + change shape).
 */
export function useMagnetic<T extends HTMLElement>({
  radius = 140,
  strength = 0.35,
  disabled = false,
}: Options = {}) {
  const ref = useRef<T | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el || disabled) return;

    const canHover = window.matchMedia('(hover: hover)').matches;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!canHover || reduce) return;

    el.dataset.magnetic = 'true';

    let raf = 0;
    let targetX = 0;
    let targetY = 0;
    let currentX = 0;
    let currentY = 0;

    const animate = () => {
      currentX += (targetX - currentX) * 0.18;
      currentY += (targetY - currentY) * 0.18;
      el.style.transform = `translate3d(${currentX}px, ${currentY}px, 0)`;
      if (Math.abs(targetX - currentX) > 0.1 || Math.abs(targetY - currentY) > 0.1) {
        raf = requestAnimationFrame(animate);
      } else {
        el.style.transform = `translate3d(${targetX}px, ${targetY}px, 0)`;
        raf = 0;
      }
    };

    const onMove = (e: PointerEvent) => {
      const rect = el.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      const dx = e.clientX - cx;
      const dy = e.clientY - cy;
      const distance = Math.hypot(dx, dy);

      if (distance < radius) {
        targetX = dx * strength;
        targetY = dy * strength;
      } else {
        targetX = 0;
        targetY = 0;
      }
      if (!raf) raf = requestAnimationFrame(animate);
    };

    const onLeave = () => {
      targetX = 0;
      targetY = 0;
      if (!raf) raf = requestAnimationFrame(animate);
    };

    window.addEventListener('pointermove', onMove);
    document.addEventListener('pointerleave', onLeave);

    return () => {
      window.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerleave', onLeave);
      if (raf) cancelAnimationFrame(raf);
      el.style.transform = '';
      delete el.dataset.magnetic;
    };
  }, [radius, strength, disabled]);

  return ref;
}
